
import {
    DirectionsWalk as Footprints,
    Image as ImageIcon,
    Layers,
    Terrain as Mountain,
    Public as PublicIcon,
    Redo as Redo2,
    Save,
    TextFields as Type,
    Undo as Undo2,
} from "@mui/icons-material";
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { BrushPanel } from "./toolbar/BrushPanel";
import { SymbolPanel } from "./toolbar/SymbolPanel";
import { WorldPanel } from "./toolbar/WorldPanel";
import { PathsPanel } from "./toolbar/PathsPanel";
import { LabelsPanel } from "./toolbar/LabelsPanel";
import { OverlayPanel } from "./toolbar/OverlayPanel";
import type { PlacedSymbol, MapSection, LandTool, SymbolMode, MapEntityPoint, MapEntityArea, MapEntityRef, MapAreaStyle, MapEntityPath, MapPathStyle, MapLabel, LayerVisibility } from "./canvas/types";
import type { MapPathType, MapRoadQuality } from "./hooks/use-map-paths";

interface ToolbarProps {
  activeSection: MapSection;
  setActiveSection: (section: MapSection) => void;
  activeLandTool: LandTool;
  setActiveLandTool: (tool: LandTool) => void;
  paintOpacity: number;
  setPaintOpacity: (value: number) => void;
  brush: string;
  setBrush: (brush: string) => void;
  brushSize: number;
  setBrushSize: (size: number) => void;
  roughness: number;
  setRoughness: (value: number) => void;
  tool: 'pencil' | 'fill' | 'eraser';
  setTool: (tool: 'pencil' | 'fill' | 'eraser') => void;
  onSave: () => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomReset: () => void;
  isSaving?: boolean;
  // Symbol Options
  activeSymbol: string;
  activeSymbols?: string[];
  setActiveSymbol: (id: string) => void;
  setActiveSymbols?: (symbols: string[]) => void;
  activeSymbolCategory: string;
  setActiveSymbolCategory: (category: string) => void;
  symbolMode?: SymbolMode;
  setSymbolMode?: (mode: SymbolMode) => void;
  stampDensity?: number;
  setStampDensity?: (density: number) => void;
  symbolSize?: number;
  setSymbolSize?: (size: number) => void;
  symbolStyle?: 'colored' | 'empty';
  setSymbolStyle?: (style: 'colored' | 'empty') => void;
  processedSheets: Record<string, HTMLCanvasElement | HTMLImageElement>;
  placedSymbols?: PlacedSymbol[];
  // World Options
  mapEntityPoints?: MapEntityPoint[];
  mapEntityAreas?: MapEntityArea[];
  isPlacingPoint?: boolean;
  isDrawingArea?: boolean;
  onStartPlacingPoint?: () => void;
  onStopPlacingPoint?: () => void;
  onStartDrawingArea?: () => void;
  onStopDrawingArea?: () => void;
  onDeletePoint?: (uid: string) => void;
  onDeleteArea?: (uid: string) => void;
  onChangeLabelPoint?: (uid: string, label: string) => void;
  onChangeEntityPoint?: (uid: string, ref: MapEntityRef) => void;
  onChangeEntityArea?: (uid: string, ref: MapEntityRef) => void;
  onUpdateAreaStyle?: (uid: string, style: Partial<MapAreaStyle>) => void;
  selectedWorldUid?: string | null;
  onSelectWorldUid?: (uid: string | null) => void;
  currentLocationId?: string;
  // Path options
  mapEntityPaths?: MapEntityPath[];
  isDrawingPath?: boolean;
  selectedPathUid?: string | null;
  onSelectPath?: (uid: string | null) => void;
  onStartDrawingPath?: (pathType: MapPathType, roadQuality?: MapRoadQuality) => void;
  onStopDrawingPath?: () => void;
  onDeletePath?: (uid: string) => void;
  onUpdatePathStyle?: (uid: string, style: Partial<MapPathStyle>) => void;
  onUpdatePathMeta?: (uid: string, meta: { name?: string; description?: string }) => void;
  onUpdatePathType?: (uid: string, pathType: MapPathType, roadQuality?: MapRoadQuality) => void;
  // Labels
  mapLabels?: MapLabel[];
  selectedLabelUid?: string | null;
  onUpdateLabel?: (uid: string, updates: Partial<Omit<MapLabel, 'uid'>>) => void;
  onDeleteLabel?: (uid: string) => void;
  onCommitLabel?: () => void;
  // Layers
  layerVisibility?: LayerVisibility;
  setLayerVisibility?: React.Dispatch<React.SetStateAction<LayerVisibility>>;
}

const sections = [
    { id: 'land', icon: Mountain, label: 'Land' },
    { id: 'paths', icon: Footprints, label: 'Paths' },
    { id: 'symbols', icon: ImageIcon, label: 'Symbols' },
    { id: 'labels', icon: Type, label: 'Labels' },
    { id: 'overlay', icon: Layers, label: 'Overlay' },
    { id: 'world', icon: PublicIcon, label: 'World' },
] as const;

export function ToolbarSidebar({
    activeSection,
    setActiveSection,
    onSave,
    onUndo,
    onRedo,
    canUndo,
    canRedo,
    isSaving = false,
    isCustomUpload = false,
}: Pick<ToolbarProps, 'activeSection' | 'setActiveSection' | 'onSave' | 'onUndo' | 'onRedo' | 'canUndo' | 'canRedo' | 'isSaving' | 'activeSymbol' | 'setActiveSymbol'> & { isCustomUpload?: boolean }) {
    const { t } = useTranslation();
    return (
        <Paper sx={{ width: 64, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2, gap: 2, borderRadius: 2 }} elevation={2}>
            <ToggleButtonGroup
                orientation="vertical"
                value={activeSection}
                exclusive
                onChange={(_, newSection) => newSection && setActiveSection(newSection)}
                aria-label="sections"
                sx={{ gap: 1, '& .MuiToggleButton-root': { border: 0, borderRadius: 1 } }}
            >
                {sections.map((s) => {
                    const Icon = s.icon;
                    const isDisabled = isCustomUpload && s.id === 'land';
                    return (
                        <Tooltip key={s.id} title={isDisabled ? t('map_creator.toolbar.land_disabled') : t(`map_creator.toolbar.${s.id}`)} placement="right">
                             <ToggleButton value={s.id} aria-label={t(`map_creator.toolbar.${s.id}`)} disabled={isDisabled} sx={{ width: 40, height: 40 }}>
                                <Icon sx={{ fontSize: '20px' }} />
                            </ToggleButton>
                        </Tooltip>
                    );
                })}
            </ToggleButtonGroup>

            <Box mt="auto" display="flex" flexDirection="column" gap={1}>
                <Tooltip title={t('map_creator.toolbar.undo')} placement="right">
                    <span>
                         <IconButton onClick={onUndo} disabled={!canUndo}>
                            <Undo2 sx={{ fontSize: '18px' }} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={t('map_creator.toolbar.redo')} placement="right">
                    <span>
                         <IconButton onClick={onRedo} disabled={!canRedo}>
                            <Redo2 sx={{ fontSize: '18px' }} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Divider sx={{ my: 1 }} />
                <Tooltip title={isSaving ? t('map_creator.toolbar.preserving') : t('map_creator.toolbar.save')} placement="right">
                    <span>
                        <IconButton
                            onClick={onSave}
                            disabled={isSaving}
                            color="primary"
                        >
                            {isSaving ? <CircularProgress size={20} /> : <Save sx={{ fontSize: '20px' }} />}
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        </Paper>
    );
}

export function ToolbarPanel(props: Omit<ToolbarProps, 'onSave' | 'onUndo' | 'onRedo' | 'canUndo' | 'canRedo' | 'isSaving' | 'setActiveSection'>) {
    const {
        activeSection, onZoomIn, onZoomOut, onZoomReset,
    } = props;
    const { t } = useTranslation();
 
    return (
        <Paper sx={{ width: 320, height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }} elevation={2}>
            <Box p={2} height="100%" display="flex" flexDirection="column">
                <Box borderBottom={1} borderColor="divider" pb={1} mb={2}>
                    <Typography variant="overline" color="text.secondary" fontWeight="bold" fontSize="0.7rem" letterSpacing={1}>
                        {t(`map_creator.toolbar.${activeSection}`)}
                    </Typography>
                </Box>
 
                <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', pr: 0.5 }}>
                    {activeSection === 'land' && (
                        <BrushPanel
                            activeLandTool={props.activeLandTool}
                            setActiveLandTool={props.setActiveLandTool}
                            brush={props.brush}
                            setBrush={props.setBrush}
                            brushSize={props.brushSize}
                            setBrushSize={props.setBrushSize}
                            roughness={props.roughness}
                            setRoughness={props.setRoughness}
                            paintOpacity={props.paintOpacity}
                            setPaintOpacity={props.setPaintOpacity}
                        />
                    )}

                    {activeSection === 'symbols' && (
                        <SymbolPanel
                            activeSymbol={props.activeSymbol}
                            setActiveSymbol={props.setActiveSymbol}
                            activeSymbolCategory={props.activeSymbolCategory}
                            setActiveSymbolCategory={props.setActiveSymbolCategory}
                            brushSize={props.brushSize}
                            setBrushSize={props.setBrushSize}
                            paintOpacity={props.paintOpacity}
                            setPaintOpacity={props.setPaintOpacity}
                            symbolMode={props.symbolMode}
                            setSymbolMode={props.setSymbolMode}
                            stampDensity={props.stampDensity}
                            setStampDensity={props.setStampDensity}
                            symbolSize={props.symbolSize}
                            setSymbolSize={props.setSymbolSize}
                            symbolStyle={props.symbolStyle}
                            setSymbolStyle={props.setSymbolStyle}
                            activeSymbols={props.activeSymbols}
                            setActiveSymbols={props.setActiveSymbols}
                            processedSheets={props.processedSheets}
                            placedSymbols={props.placedSymbols}
                        />
                    )}

                    {activeSection === 'paths' && (
                        <PathsPanel
                            paths={props.mapEntityPaths ?? []}
                            isDrawingPath={props.isDrawingPath ?? false}
                            selectedPathUid={props.selectedPathUid ?? null}
                            onSelectPath={props.onSelectPath ?? (() => {})}
                            onStartDrawing={props.onStartDrawingPath ?? (() => {})}
                            onStopDrawing={props.onStopDrawingPath ?? (() => {})}
                            onDeletePath={props.onDeletePath ?? (() => {})}
                            onUpdateStyle={props.onUpdatePathStyle ?? (() => {})}
                            onUpdateMeta={props.onUpdatePathMeta ?? (() => {})}
                            onUpdateType={props.onUpdatePathType ?? (() => {})}
                        />
                    )}

                    {activeSection === 'labels' && (
                        <LabelsPanel
                            mapLabels={props.mapLabels ?? []}
                            selectedLabelUid={props.selectedLabelUid ?? null}
                            updateLabel={props.onUpdateLabel ?? (() => {})}
                            deleteLabel={props.onDeleteLabel ?? (() => {})}
                            onCommit={props.onCommitLabel ?? (() => {})}
                        />
                    )}

                    {activeSection === 'world' && props.mapEntityPoints !== undefined && (
                        <WorldPanel
                            points={props.mapEntityPoints ?? []}
                            areas={props.mapEntityAreas ?? []}
                            isPlacingPoint={props.isPlacingPoint ?? false}
                            isDrawingArea={props.isDrawingArea ?? false}
                            onStartPlacingPoint={props.onStartPlacingPoint ?? (() => {})}
                            onStopPlacingPoint={props.onStopPlacingPoint ?? (() => {})}
                            onStartDrawingArea={props.onStartDrawingArea ?? (() => {})}
                            onStopDrawingArea={props.onStopDrawingArea ?? (() => {})}
                            onDeletePoint={props.onDeletePoint ?? (() => {})}
                            onDeleteArea={props.onDeleteArea ?? (() => {})}
                            onChangeLabelPoint={props.onChangeLabelPoint ?? (() => {})}
                            onChangeEntityPoint={props.onChangeEntityPoint ?? (() => {})}
                            onChangeEntityArea={props.onChangeEntityArea ?? (() => {})}
                            onUpdateAreaStyle={props.onUpdateAreaStyle ?? (() => {})}
                            selectedWorldUid={props.selectedWorldUid ?? null}
                            onSelectWorldUid={props.onSelectWorldUid ?? (() => {})}
                            currentLocationId={props.currentLocationId}
                        />
                    )}

                    {activeSection === 'overlay' && props.layerVisibility && props.setLayerVisibility && (
                        <OverlayPanel
                            layerVisibility={props.layerVisibility}
                            setLayerVisibility={props.setLayerVisibility}
                        />
                    )}

                    {activeSection !== 'land' && activeSection !== 'symbols' && activeSection !== 'world' && activeSection !== 'paths' && activeSection !== 'labels' && activeSection !== 'overlay' && (
                        <Box sx={{
                            height: 200,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px dashed grey',
                            borderRadius: 1,
                            bgcolor: 'action.hover',
                            p: 2
                        }}>
                             <Typography variant="caption" color="text.secondary" textAlign="center">
                                {t('map_creator.toolbar.coming_soon', {
                                    section: {
                                        land: t('map_creator.toolbar.land'),
                                        paths: t('map_creator.toolbar.paths'),
                                        symbols: t('map_creator.toolbar.symbols'),
                                        labels: t('map_creator.toolbar.labels'),
                                        overlay: t('map_creator.toolbar.overlay'),
                                        world: t('map_creator.toolbar.world')
                                    }[activeSection]
                                })}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Box pt={2} mt="auto">
                    <Divider sx={{ mb: 2 }} />
                    <Stack spacing={1}>
                        <Typography variant="overline" color="text.secondary" fontWeight="bold" fontSize="0.7rem">{t('map_creator.toolbar.navigation')}</Typography>
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" size="small" onClick={onZoomOut} fullWidth sx={{ fontSize: '0.7rem' }}>{t('map_creator.toolbar.zoom_out')}</Button>
                            <Button variant="outlined" size="small" onClick={onZoomReset} fullWidth sx={{ fontSize: '0.7rem' }}>{t('map_creator.toolbar.reset')}</Button>
                            <Button variant="outlined" size="small" onClick={onZoomIn} fullWidth sx={{ fontSize: '0.7rem' }}>{t('map_creator.toolbar.zoom_in')}</Button>
                        </Stack>
                    </Stack>
                </Box>
            </Box>
        </Paper>
    );
}

export function Toolbar(props: ToolbarProps) {
  return (
    <Stack direction="row" spacing={2} height="100%">
        <ToolbarSidebar {...props} />
        <ToolbarPanel {...props} />
    </Stack>
  );
}
